if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // The size-quantity grid lives outside this.form (sibling section block), so
        // delegate from document rather than this.form to catch its input events.
        document.addEventListener('input', (event) => {
          if (!event.target.classList.contains('size-quantity-grid__input')) return;
          if (event.target.closest('variant-selects')?.id !== `variant-selects-${this.dataset.sectionId}`) return;
          this.clampSizeGridInput(event.target);
        });
      }

      // The size-quantity grid is rendered by product-variant-picker, a sibling
      // section block to this <product-form>, not a descendant of it - it can't
      // be found via this.form.querySelector.
      get sizeQuantityGrid() {
        const variantSelects = document.getElementById(`variant-selects-${this.dataset.sectionId}`);
        return variantSelects ? variantSelects.querySelector('.size-quantity-grid') : null;
      }

      getSizeGridItems() {
        return Array.from(this.sizeQuantityGrid.querySelectorAll('.size-quantity-grid__input'))
          .map((input) => ({ variantId: input.dataset.variantId, quantity: parseInt(input.value, 10) || 0 }))
          .filter((item) => item.variantId && item.quantity > 0);
      }

      clampSizeGridInput(input) {
        const errorEl = input.parentElement.querySelector('.size-quantity-grid__error');
        const max = input.dataset.inventoryQuantity !== undefined ? parseInt(input.dataset.inventoryQuantity, 10) : null;
        let value = parseInt(input.value, 10);

        if (isNaN(value) || value < 0) {
          input.value = '';
          this.setSizeGridInputState(input, errorEl, 'none');
          return;
        }

        // The browser's native spinner never lets value exceed max on its own, so
        // value > max only happens when the number was typed in directly - that's
        // a real overflow attempt. Landing exactly on max (by typing or spinning)
        // is just using up the last available unit, not an error.
        const exceeded = max !== null && value > max;
        if (exceeded) {
          input.value = max;
          value = max;
        }

        if (exceeded) {
          this.setSizeGridInputState(input, errorEl, 'error');
        } else if (max !== null && value >= max) {
          this.setSizeGridInputState(input, errorEl, 'max');
        } else {
          this.setSizeGridInputState(input, errorEl, 'none');
        }
      }

      setSizeGridInputState(input, errorEl, state) {
        input.classList.toggle('size-quantity-grid__input--error', state === 'error');
        input.classList.toggle('size-quantity-grid__input--max', state === 'max');

        if (!errorEl) return;
        errorEl.classList.toggle('size-quantity-grid__error--error', state === 'error');
        errorEl.classList.toggle('size-quantity-grid__error--visible', state !== 'none');

        if (state === 'error') errorEl.textContent = '0 left';
        else if (state === 'max') errorEl.textContent = 'Max available';
      }

      buildSizeGridFormData(items) {
        const propertiesFormData = new FormData(this.form);
        const formData = new FormData(this.form);
        formData.delete('id');
        formData.delete('quantity');

        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, item.variantId);
          formData.append(`items[${index}][quantity]`, item.quantity);

          for (const [key, value] of propertiesFormData.entries()) {
            if (!key.startsWith('properties[')) continue;
            formData.append(key.replace('properties[', `items[${index}][properties][`), value);
          }
        });

        return formData;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        let sizeGridItems = null;
        if (this.sizeQuantityGrid) {
          sizeGridItems = this.getSizeGridItems();
          if (sizeGridItems.length === 0) {
            this.handleErrorMessage('Please enter a quantity for at least one size.');
            return;
          }
        }

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = sizeGridItems ? this.buildSizeGridFormData(sizeGridItems) : new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const primaryVariantId = sizeGridItems ? sizeGridItems[0].variantId : formData.get('id');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: primaryVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (response.items && !response.key) {
              const lastItem = response.items[response.items.length - 1];
              response.key = lastItem?.key;
              response.id = lastItem?.id;
            }

            if (sizeGridItems) {
              const addedByVariant = new Map(sizeGridItems.map((item) => [item.variantId, item.quantity]));
              this.sizeQuantityGrid.querySelectorAll('.size-quantity-grid__input').forEach((input) => {
                input.value = '';
                this.setSizeGridInputState(input, input.parentElement.querySelector('.size-quantity-grid__error'), 'none');

                const added = addedByVariant.get(input.dataset.variantId);
                if (!added || input.dataset.inventoryQuantity === undefined) return;

                const newMax = Math.max(0, parseInt(input.dataset.inventoryQuantity, 10) - added);
                const newCartQty = (parseInt(input.dataset.cartQuantity, 10) || 0) + added;
                input.dataset.inventoryQuantity = newMax;
                input.dataset.cartQuantity = newCartQty;
                input.max = newMax;
              });
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: primaryVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
